// Ansicht Anlagenüberwachung (Operator-HMI-Überblick).
//
// Dichte Leitwarten-Optik: der Signalkatalog des aktiven Projekts, nach Anlagenbereich
// gruppiert, als Kachelraster mit großem Wert + Einheit. Je Messgröße wird der jüngste
// Tick-Wert clientseitig gegen die Katalog-Grenzen (Lo/LoLo/Hi/HiHi) bewertet und eingefärbt
// (normal/Warnung/Alarm); oben steht die Alarm-Zusammenfassung. Aktor-Sollwerte
// (direction === 'output') zeigen ihren Regelschleifen-Modus (Auto/Hand/fail-safe) samt
// Klemmung-/Rate-Flag. Betriebszustand (Verbindung, Watchdog) und Lauf-Start/Stop teilen sich
// die WS-/Lauf-Anbindung mit der Monitor-Ansicht (useMonitorRun).

import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { useI18n } from '../i18n';
import { useStore } from '../state/store';
import { useMonitorRun, type ConnState } from '../state/useMonitorRun';
import type { CatalogVariable, MonitoringConfig, TickValue, TileDisplayMode } from '../api/types';
import { anzeigeMitBeleg, anzeigeName } from '../graph/anzeige';
import { getMe, getMonitoring, hasEditRight, putMonitoring } from '../api/client';
import { Button, EmptyState, PanelHeading } from '../components/ui';
import {
  BarInstrument,
  DigitalInstrument,
  GaugeInstrument,
  LampInstrument,
} from '../components/instruments';
import { TrendChart, type Series } from '../components/TrendChart';
import {
  deriveMode,
  evaluate,
  groupByArea,
  limitFractions,
  rangeFraction,
  summarize,
  type EvaluatedVar,
  type LimitState,
} from './dashboard';

// Anzeigeart der Messgrößen (BORIS-artige Instrumentenwahl). 'numeric' = die klassische Kachel,
// 'trend' = ein kompakter Verlauf der letzten Werte. 'table' bleibt eine GLOBALE Gesamtansicht
// (keine Pro-Kachel-Wahl) — deshalb ist die Kachel-Anzeigeart der engere Typ TileDisplayMode.
type DisplayMode = TileDisplayMode | 'table';
// Pro-Kachel wählbare Anzeigearten (ohne 'table').
const TILE_MODES: TileDisplayMode[] = ['numeric', 'gauge', 'trend', 'bar', 'lamp', 'digital'];
// Global (Default/„alle setzen") wählbare Anzeigearten — inkl. der Gesamt-Tabelle.
const DISPLAY_MODES: DisplayMode[] = [...TILE_MODES, 'table'];

// Zustandsfarbe des Trend-Verlaufs (konsistent zu den anderen Instrumenten).
const TREND_STATE_COLOR: Record<LimitState, string> = {
  normal: '#C49B18', // bf-gold
  warn: '#FBBF24', // amber-400
  alarm: '#F87171', // red-400
  unknown: '#7A8FAF', // bf-muted
};

// Farbcodierung der Grenzwert-Zustände — sparsam auf Navy (grün/gelb/rot nur, wo nötig).
const STATE_STYLE: Record<LimitState, { border: string; dot: string; text: string }> = {
  normal: { border: 'border-bf-border', dot: 'bg-emerald-400', text: 'text-bf-gold' },
  warn: { border: 'border-amber-500/70', dot: 'bg-amber-400', text: 'text-amber-300' },
  alarm: { border: 'border-red-500/80', dot: 'bg-red-400', text: 'text-red-300' },
  unknown: { border: 'border-bf-border', dot: 'bg-bf-muted', text: 'text-bf-muted' },
};

function formatValue(v: number): string {
  if (Math.abs(v) >= 1000) return v.toFixed(0);
  if (Math.abs(v) >= 1) return v.toFixed(2);
  return v.toFixed(3);
}

export function DashboardView(): JSX.Element {
  const { t } = useI18n();
  const { activeProject, activeProjectId, catalog, reportError } = useStore();
  const { runId, owned, conn, busy, series, latest, safety, start, stop, reconnect } =
    useMonitorRun();
  // Globale Vorgabe (Default) für Kacheln ohne eigene Wahl — zugleich die „alle setzen"-Auswahl.
  const [display, setDisplay] = useState<DisplayMode>('numeric');
  // Anzeigeart je Tag (überschreibt die globale Vorgabe). Aus monitoring.json initialisiert und
  // — mit Recht „editieren" — entprellt dorthin zurückgeschrieben.
  const [tileModes, setTileModes] = useState<Record<string, TileDisplayMode>>({});
  const [canEdit, setCanEdit] = useState(false);
  // Die zuletzt gelesene Monitoring-Konfiguration behalten, damit ein PUT NUR displayTypes ändert
  // und die übrigen Felder (Dashboards, Alarm-Politik, Übersteuerungen) erhält.
  const monitoringRef = useRef<MonitoringConfig | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ein lesend angehängter Fremdlauf (owned === false) blockiert die Steuerung: kein Start (es
  // läuft schon einer), kein Stop (nicht unser Lauf).
  const attached = runId !== null && !owned;

  // Recht „editieren" aus dem angemeldeten Principal ableiten (wie in HistoryView). Ohne das Recht
  // bleibt die Anzeigeart-Wahl rein lokal (kein PUT); das Backend sichert den PUT ohnehin mit 403.
  useEffect(() => {
    let cancelled = false;
    getMe()
      .then((me) => !cancelled && setCanEdit(hasEditRight(me.roles)))
      .catch(() => !cancelled && setCanEdit(false));
    return () => {
      cancelled = true;
    };
  }, []);

  // Beim Projektwechsel die Monitoring-Konfiguration laden und die Pro-Tag-Anzeigearten daraus
  // initialisieren. Der Default-Selektor bleibt unberührt (er ist nur die Vorgabe für den Rest).
  useEffect(() => {
    if (!activeProjectId) {
      monitoringRef.current = null;
      setTileModes({});
      return;
    }
    let cancelled = false;
    void getMonitoring(activeProjectId)
      .then((cfg) => {
        if (cancelled) return;
        monitoringRef.current = cfg;
        setTileModes({ ...(cfg.displayTypes ?? {}) });
      })
      .catch(() => {
        // Kein monitoring.json lesbar → mit leerer Zuordnung starten (alles folgt dem Default).
        if (cancelled) return;
        monitoringRef.current = null;
        setTileModes({});
      });
    return () => {
      cancelled = true;
      if (saveTimerRef.current !== null) clearTimeout(saveTimerRef.current);
    };
  }, [activeProjectId]);

  // Eine geänderte Pro-Tag-Zuordnung entprellt nach monitoring.json zurückschreiben — nur das
  // displayTypes-Feld ändern, die übrigen Monitoring-Felder erhalten. Nur mit Recht „editieren".
  const persist = useCallback(
    (next: Record<string, TileDisplayMode>) => {
      if (!activeProjectId || !canEdit) return;
      if (saveTimerRef.current !== null) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        saveTimerRef.current = null;
        const base: MonitoringConfig = monitoringRef.current ?? {
          schema_version: 1,
          dashboards: [],
          alarmPolicy: { requireAck: true, escalationSec: 0 },
          thresholdOverrides: [],
        };
        const cfg: MonitoringConfig = { ...base, displayTypes: next };
        monitoringRef.current = cfg;
        void putMonitoring(activeProjectId, cfg).catch((err) =>
          reportError(err, t('dashboard.display.saveFailed')),
        );
      }, 400) as unknown as ReturnType<typeof setTimeout>;
    },
    [activeProjectId, canEdit, reportError, t],
  );

  // Anzeigeart einer einzelnen Kachel setzen (lokal sofort, persistiert entprellt).
  const setTileMode = useCallback(
    (tag: string, mode: TileDisplayMode) => {
      setTileModes((prev) => {
        const next = { ...prev, [tag]: mode };
        persist(next);
        return next;
      });
    },
    [persist],
  );

  // Anzeigeart je Tag (eigene Wahl vor globaler Vorgabe). Die globale 'table'-Ansicht schaltet
  // alle Kacheln um — für eine Kachel selbst gilt dann fail-safe die klassische 'numeric'.
  const modeFor = useCallback(
    (tag: string): TileDisplayMode =>
      tileModes[tag] ?? (display === 'table' ? 'numeric' : display),
    [tileModes, display],
  );

  if (!activeProject) {
    return (
      <div className="p-8">
        <EmptyState title={t('common.noProject')} hint={t('common.noProjectHint')} />
      </div>
    );
  }

  const variables = catalog?.variables ?? [];
  const inputs = variables.filter((v) => v.direction === 'input');
  const outputs = variables.filter((v) => v.direction === 'output');

  // Messgrößen bewerten (Grenzwert-Zustand) und nach Bereich gruppieren.
  const evaluatedInputs = evaluate(inputs, latest);
  const summary = summarize(evaluatedInputs);
  const evalByTag = new Map<string, EvaluatedVar>(evaluatedInputs.map((e) => [e.variable.tag, e]));
  const areas = groupByArea(inputs);

  const watchdog = safety?.watchdog;

  return (
    <div className="flex h-full flex-col" data-testid="dashboard-view">
      {/* Kopfleiste: Alarm-Zusammenfassung, Betriebszustand, Lauf-Steuerung */}
      <div className="flex flex-wrap items-center gap-4 border-b border-bf-border px-4 py-3">
        <AlarmSummaryBadge alarms={summary.alarms} warnings={summary.warnings} />
        <div className="h-6 w-px bg-bf-border" />
        <span data-testid="dashboard-conn" data-conn={conn}>
          <ConnBadge state={conn} />
        </span>
        <WatchdogBadge state={watchdog} />
        {conn === 'lost' && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-amber-300">{t('monitor.lost')}</span>
            <Button onClick={reconnect}>{t('monitor.reconnect')}</Button>
          </div>
        )}
        <label className="flex items-center gap-1 text-xs text-bf-muted">
          {t('dashboard.display.label')}
          <select
            data-testid="dashboard-display"
            className="rounded border border-bf-border bg-bf-navy-light px-1.5 py-1 text-xs text-bf-white"
            value={display}
            onChange={(e) => {
              const m = e.target.value as DisplayMode;
              setDisplay(m);
              // Der globale Selektor ist Default UND „alle setzen": eine Kachel-Anzeigeart (nicht
              // die Gesamt-Tabelle) übernimmt alle bestehenden Pro-Tag-Wahlen auf diesen Modus.
              if (m !== 'table') {
                setTileModes((prev) => {
                  if (Object.keys(prev).length === 0) return prev;
                  const next: Record<string, TileDisplayMode> = {};
                  for (const tag of Object.keys(prev)) next[tag] = m;
                  persist(next);
                  return next;
                });
              }
            }}
          >
            {DISPLAY_MODES.map((m) => (
              <option key={m} value={m}>
                {t(`dashboard.display.${m}`)}
              </option>
            ))}
          </select>
        </label>
        <div className="ml-auto flex items-center gap-3">
          {runId && <span className="text-xs text-bf-muted">{runId}</span>}
          {attached ? (
            <span data-testid="dashboard-attached" className="text-xs text-bf-muted">
              {t('monitor.attached')}
            </span>
          ) : !runId ? (
            <span data-testid="dashboard-start">
              <Button variant="primary" onClick={() => void start()} disabled={busy}>
                {t('monitor.start')}
              </Button>
            </span>
          ) : (
            <span data-testid="dashboard-stop">
              <Button variant="danger" onClick={() => void stop()} disabled={busy}>
                {t('monitor.stop')}
              </Button>
            </span>
          )}
        </div>
      </div>

      {variables.length === 0 ? (
        <div className="p-8">
          <EmptyState title={t('dashboard.empty')} hint={t('dashboard.emptyHint')} />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-4">
          {/* Anlagenüberblick: Messgrößen nach Bereich gruppiert — oder als sortierbare Tabelle */}
          <PanelHeading>{t('dashboard.overview')}</PanelHeading>
          {display === 'table' ? (
            <div className="mb-6">
              <SignalTable evaluated={evaluatedInputs} />
            </div>
          ) : (
            <div className="mb-6 flex flex-col gap-5">
              {areas.map(({ area, variables: vars }) => (
                <section key={area} data-testid={`dashboard-area-${area}`}>
                  <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-bf-muted">
                    {area}
                  </h4>
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(11rem,1fr))] gap-2">
                    {vars.map((v) => {
                      const e = evalByTag.get(v.tag);
                      return (
                        <VarTile
                          key={v.tag}
                          variable={v}
                          tick={e?.tick}
                          state={e?.state ?? 'unknown'}
                          mode={modeFor(v.tag)}
                          points={series.get(v.tag)}
                          onModeChange={(m) => setTileMode(v.tag, m)}
                        />
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          )}

          {/* Regelschleifen-Status je Aktor-Sollwert */}
          <PanelHeading>{t('dashboard.loops')}</PanelHeading>
          {outputs.length === 0 ? (
            <p className="text-xs text-bf-muted">{t('dashboard.noLoops')}</p>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-2">
              {outputs.map((v) => (
                <LoopTile key={v.tag} variable={v} tick={latest.get(v.tag)} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Eine Messgrößen-Kachel: Kopf (Tag + Statuspunkt) plus die gewählte Anzeigeart (Ziffer/Tacho/
// Balken/Lampe/Digital). 'numeric' ist die klassische Kachel mit Wert + Bereichsbalken.
function VarTile({
  variable,
  tick,
  state,
  mode,
  points,
  onModeChange,
}: {
  variable: CatalogVariable;
  tick: TickValue | undefined;
  state: LimitState;
  mode: TileDisplayMode;
  // Live-Verlauf des Tags (aus useMonitorRun.series) — für den 'trend'-Modus.
  points: { t: number; value: number }[] | undefined;
  onModeChange: (mode: TileDisplayMode) => void;
}): JSX.Element {
  const { t } = useI18n();
  const style = STATE_STYLE[state];
  const frac = rangeFraction(variable, tick?.value);
  const unit = tick?.unit ?? variable.unit;
  const input = {
    value: tick?.value,
    min: variable.range.min,
    max: variable.range.max,
    frac,
    state,
    unit,
    ...limitFractions(variable),
  };
  return (
    <div
      data-testid={`dashboard-var-${variable.tag}`}
      data-state={state}
      data-mode={mode}
      data-value={tick ? String(tick.value) : ''}
      className={`flex flex-col rounded border ${style.border} bg-bf-navy-light px-3 py-2`}
    >
      <div className="flex items-center justify-between gap-1">
        {/* Kurzlabel groß, Bezeichner klein daneben (#373): das Label sagt, WAS gemessen wird,
            der Bezeichner belegt, WELCHE Messstelle gemeint ist. Ohne Label steht dort der
            Bezeichner allein — nie eine leere Zeile. */}
        <span className="flex min-w-0 items-baseline gap-1">
          <span className="truncate text-xs text-bf-muted" title={anzeigeMitBeleg(variable)}>
            {anzeigeName(variable)}
          </span>
          {variable.label ? (
            <span className="shrink-0 text-[10px] text-bf-muted/70">{variable.tag}</span>
          ) : null}
        </span>
        <div className="flex shrink-0 items-center gap-1">
          {/* Eigener kleiner Anzeigeart-Selektor je Kachel (überschreibt die globale Vorgabe). */}
          <select
            data-testid={`tile-display-${variable.tag}`}
            aria-label={t('dashboard.display.perTile')}
            title={t('dashboard.display.perTile')}
            className="rounded border border-bf-border bg-bf-navy px-1 py-0.5 text-[10px] text-bf-muted hover:text-bf-white"
            value={mode}
            onChange={(e) => onModeChange(e.target.value as TileDisplayMode)}
          >
            {TILE_MODES.map((m) => (
              <option key={m} value={m}>
                {t(`dashboard.display.${m}`)}
              </option>
            ))}
          </select>
          <span className={`inline-block h-2 w-2 rounded-full ${style.dot}`} />
        </div>
      </div>

      {mode === 'trend' && (
        <div className="mt-2 h-20" data-testid={`tile-trend-${variable.tag}`}>
          <TrendTile
            tag={variable.tag}
            points={points}
            state={state}
            value={tick?.value}
            unit={unit}
            emptyLabel={t('monitor.noData')}
          />
        </div>
      )}
      {mode === 'gauge' && (
        <div className="mt-1 h-20">
          <GaugeInstrument {...input} />
        </div>
      )}
      {mode === 'bar' && (
        <div className="mt-2">
          <BarInstrument {...input} />
        </div>
      )}
      {mode === 'lamp' && (
        <div className="mt-2">
          <LampInstrument {...input} />
        </div>
      )}
      {mode === 'digital' && (
        <div className="mt-2">
          <DigitalInstrument {...input} />
        </div>
      )}
      {mode === 'numeric' && (
        <>
          <div className="mt-1 flex items-baseline gap-1">
            <span className={`text-2xl font-semibold tabular-nums ${style.text}`}>
              {tick ? formatValue(tick.value) : '—'}
            </span>
            <span className="text-xs text-bf-muted">{unit}</span>
          </div>
          {/* Bereichsbalken: Position im Katalog-Bereich [min,max] */}
          <div className="mt-2 h-1 w-full overflow-hidden rounded bg-bf-border">
            {frac !== null && (
              <div
                className={`h-full ${state === 'alarm' ? 'bg-red-400' : state === 'warn' ? 'bg-amber-400' : 'bg-bf-gold'}`}
                style={{ width: `${Math.round(frac * 100)}%` }}
              />
            )}
          </div>
        </>
      )}

      {state === 'alarm' && (
        <span className="mt-1 text-[10px] font-medium uppercase tracking-wide text-red-300">
          {t('dashboard.state.alarm')}
        </span>
      )}
      {state === 'warn' && (
        <span className="mt-1 text-[10px] font-medium uppercase tracking-wide text-amber-300">
          {t('dashboard.state.warn')}
        </span>
      )}
    </div>
  );
}

// Kompakter Verlauf (Anzeigeart „Graph“) je Kachel: der Live-Verlauf des Tags aus useMonitorRun.
// Reuse des TrendChart (rein/prüfbar, keine neue Abhängigkeit) mit der Grenzwert-Zonenfarbe des
// jüngsten Zustands, plus jüngstem Wert + Einheit als kleine Kopfzeile (wie die anderen Instrumente).
function TrendTile({
  tag,
  points,
  state,
  value,
  unit,
  emptyLabel,
}: {
  tag: string;
  points: { t: number; value: number }[] | undefined;
  state: LimitState;
  value: number | undefined;
  unit: string;
  emptyLabel: string;
}): JSX.Element {
  const series: Series[] = [
    { tag, color: TREND_STATE_COLOR[state], points: points ?? [] },
  ];
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-baseline gap-1">
        <span
          className="text-sm font-semibold tabular-nums"
          style={{ color: TREND_STATE_COLOR[state] }}
        >
          {value === undefined ? '—' : formatValue(value)}
        </span>
        <span className="text-[10px] text-bf-muted">{unit}</span>
      </div>
      <div className="min-h-0 flex-1">
        <TrendChart series={series} emptyLabel={emptyLabel} width={180} height={60} />
      </div>
    </div>
  );
}

// Sortierbare Signal-Tabelle (Anzeigeart „Tabelle“): alle Messgrößen mit Bereich, Wert, Einheit
// und Grenzwert-Zustand; klick auf eine Spaltenüberschrift sortiert.
function SignalTable({ evaluated }: { evaluated: EvaluatedVar[] }): JSX.Element {
  const { t } = useI18n();
  const [sortKey, setSortKey] = useState<'tag' | 'value' | 'state'>('tag');
  const [asc, setAsc] = useState(true);
  const stateRank: Record<LimitState, number> = { alarm: 0, warn: 1, normal: 2, unknown: 3 };
  const rows = [...evaluated].sort((a, b) => {
    let d = 0;
    if (sortKey === 'tag') d = a.variable.tag.localeCompare(b.variable.tag);
    else if (sortKey === 'value') d = (a.tick?.value ?? -Infinity) - (b.tick?.value ?? -Infinity);
    else d = stateRank[a.state] - stateRank[b.state];
    return asc ? d : -d;
  });
  const th = (key: 'tag' | 'value' | 'state', label: string): JSX.Element => (
    <th
      data-testid={`signaltable-th-${key}`}
      className="cursor-pointer select-none px-3 py-1.5 text-left font-medium text-bf-muted hover:text-bf-white"
      onClick={() => {
        if (sortKey === key) setAsc((v) => !v);
        else {
          setSortKey(key);
          setAsc(true);
        }
      }}
    >
      {label}
      {sortKey === key && <span className="ml-1">{asc ? '▲' : '▼'}</span>}
    </th>
  );
  return (
    <table className="w-full border-collapse text-sm" data-testid="signal-table">
      <thead>
        <tr className="border-b border-bf-border">
          {th('tag', t('dashboard.table.tag'))}
          <th className="px-3 py-1.5 text-left font-medium text-bf-muted">
            {t('dashboard.table.name')}
          </th>
          {th('value', t('dashboard.table.value'))}
          <th className="px-3 py-1.5 text-left font-medium text-bf-muted">
            {t('dashboard.table.unit')}
          </th>
          {th('state', t('dashboard.table.state'))}
        </tr>
      </thead>
      <tbody>
        {rows.map((e) => {
          const style = STATE_STYLE[e.state];
          return (
            <tr
              key={e.variable.tag}
              data-testid={`signaltable-row-${e.variable.tag}`}
              className="border-b border-bf-border/50"
            >
              <td className="px-3 py-1.5 text-bf-white">{e.variable.tag}</td>
              {/* Namensspalte: Kurzlabel, sonst der Variablenname. Der Bezeichner steht schon
                  in der Spalte links — hier ihn zu wiederholen wäre Lärm. */}
              <td className="px-3 py-1.5 text-bf-muted">{e.variable.label ?? e.variable.name}</td>
              <td className={`px-3 py-1.5 tabular-nums ${style.text}`}>
                {e.tick ? formatValue(e.tick.value) : '—'}
              </td>
              <td className="px-3 py-1.5 text-bf-muted">{e.tick?.unit ?? e.variable.unit}</td>
              <td className="px-3 py-1.5">
                <span className="flex items-center gap-1.5">
                  <span className={`inline-block h-2 w-2 rounded-full ${style.dot}`} />
                  <span className={style.text}>{t(`dashboard.tableState.${e.state}`)}</span>
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}


// Eine Regelschleifen-Kachel: Aktor-Sollwert mit Modus (Auto/Hand/fail-safe) + Sollwert + Flags.
function LoopTile({
  variable,
  tick,
}: {
  variable: CatalogVariable;
  tick: TickValue | undefined;
}): JSX.Element {
  const { t } = useI18n();
  const mode = deriveMode(tick);
  const modeStyle: Record<string, string> = {
    auto: 'border-emerald-500/60 text-emerald-300',
    hand: 'border-amber-500/60 text-amber-300',
    failsafe: 'border-red-500/70 text-red-300',
    unknown: 'border-bf-border text-bf-muted',
  };
  const unit = tick?.unit ?? variable.unit;
  return (
    <div
      data-testid={`dashboard-loop-${variable.tag}`}
      data-mode={mode}
      className="flex flex-col rounded border border-bf-border bg-bf-navy-light px-3 py-2"
    >
      <div className="flex items-center justify-between">
        <span className="flex min-w-0 items-baseline gap-1.5">
          <span className="truncate text-sm text-bf-white" title={anzeigeMitBeleg(variable)}>
            {anzeigeName(variable)}
          </span>
          {variable.label ? (
            <span className="shrink-0 text-[10px] text-bf-muted">{variable.tag}</span>
          ) : null}
        </span>
        <span
          className={`rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${modeStyle[mode]}`}
        >
          {t(`dashboard.mode.${mode}`)}
        </span>
      </div>
      <div className="mt-1 flex items-baseline gap-1">
        <span className="text-xs text-bf-muted">{t('dashboard.setpoint')}</span>
        <span className="text-lg font-semibold tabular-nums text-bf-gold">
          {tick ? formatValue(tick.value) : '—'}
        </span>
        <span className="text-xs text-bf-muted">{unit}</span>
      </div>
      {/* Klemmung / Ratenbegrenzung des Safety-Blocks (aus dem Tick, falls gesetzt) */}
      <div className="mt-1 flex gap-1.5">
        {tick?.clamped && (
          <span className="rounded bg-red-500/20 px-1.5 py-0.5 text-[10px] text-red-300">
            {t('dashboard.clamped')}
          </span>
        )}
        {tick?.rateLimited && (
          <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] text-amber-300">
            {t('dashboard.rateLimited')}
          </span>
        )}
      </div>
    </div>
  );
}

// Alarm-Zusammenfassung oben: Anzahl aktiver Alarme/Warnungen, farbig hervorgehoben.
function AlarmSummaryBadge({
  alarms,
  warnings,
}: {
  alarms: number;
  warnings: number;
}): JSX.Element {
  const { t } = useI18n();
  const ok = alarms === 0 && warnings === 0;
  return (
    <div className="flex items-center gap-3" data-testid="dashboard-summary" data-alarms={alarms} data-warnings={warnings}>
      {ok ? (
        <span className="flex items-center gap-2 text-xs text-emerald-300">
          <span className="inline-block h-2 w-2 rounded-full bg-emerald-400" />
          {t('dashboard.allNormal')}
        </span>
      ) : (
        <>
          <span className="flex items-center gap-1.5 text-sm font-semibold text-red-300">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-red-400" />
            {alarms}
            <span className="text-xs font-normal text-bf-muted">{t('dashboard.alarms')}</span>
          </span>
          <span className="flex items-center gap-1.5 text-sm font-semibold text-amber-300">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-400" />
            {warnings}
            <span className="text-xs font-normal text-bf-muted">{t('dashboard.warnings')}</span>
          </span>
        </>
      )}
    </div>
  );
}

// Watchdog/Heartbeat-Zustand aus dem Safety-Teil (defensiv: „unbekannt", solange kein status).
function WatchdogBadge({ state }: { state: string | undefined }): JSX.Element {
  const { t } = useI18n();
  const tripped = state !== undefined && state !== 'ok';
  const color = state === undefined ? 'bg-bf-muted' : tripped ? 'bg-red-400' : 'bg-emerald-400';
  const label =
    state === undefined
      ? t('dashboard.watchdog.unknown')
      : tripped
        ? t('dashboard.watchdog.tripped')
        : t('dashboard.watchdog.ok');
  return (
    <span className="flex items-center gap-2 text-xs text-bf-muted" data-testid="dashboard-watchdog">
      <span className={`inline-block h-2 w-2 rounded-full ${color}`} />
      {t('dashboard.watchdog.label')}: {label}
    </span>
  );
}

function ConnBadge({ state }: { state: ConnState }): JSX.Element {
  const { t } = useI18n();
  const map: Record<ConnState, { label: string; color: string }> = {
    idle: { label: t('monitor.conn.idle'), color: 'bg-bf-muted' },
    connecting: { label: t('monitor.conn.connecting'), color: 'bg-amber-400' },
    live: { label: t('monitor.conn.live'), color: 'bg-emerald-400' },
    lost: { label: t('monitor.conn.lost'), color: 'bg-red-400' },
  };
  const s = map[state];
  return (
    <span className="flex items-center gap-2 text-xs text-bf-muted">
      <span className={`inline-block h-2 w-2 rounded-full ${s.color}`} />
      {s.label}
    </span>
  );
}
